import type { CanonRecord } from '../types';

const CANON_TEXT_FIELDS = ['statement', 'fact', 'text', 'description', 'content', 'summary', 'label', 'value'] as const;

export class ChapterCanonPreflightError extends Error {
  readonly code = 'CHAPTER_CANON_PREFLIGHT_ERROR';

  constructor(
    message: string,
    readonly details: {
      sourceField: string;
      chapterNumber?: number;
      runtimeType: string;
      objectKeys?: string[];
    },
  ) {
    super(message);
    this.name = 'ChapterCanonPreflightError';
  }
}

export interface NormalizedCanonEntry {
  factId: string;
  statement: string;
  knownBy: string[];
  sourceChapter?: number;
  sourceRecordId?: string;
}

export interface CanonInputShapeDiagnostic {
  chapterNumber?: number;
  sourceField: string;
  entryCount: number;
  runtimeTypes: string[];
  objectKeySets: string[][];
  normalizedCount: number;
  rejectedCount: number;
}

function runtimeTypeOf(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

function objectKeys(value: unknown): string[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return Object.keys(value as Record<string, unknown>).sort();
}

function readStringField(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

export function extractCanonStatement(entry: unknown): string | null {
  if (entry == null) return null;
  if (typeof entry === 'string') {
    return readStringField(entry);
  }
  if (typeof entry !== 'object' || Array.isArray(entry)) {
    return null;
  }

  const record = entry as Record<string, unknown>;
  for (const field of CANON_TEXT_FIELDS) {
    const candidate = readStringField(record[field]);
    if (candidate) return candidate;
  }
  return null;
}

export function extractCanonKnownBy(entry: unknown): string[] {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return ['cast'];
  }
  const record = entry as Record<string, unknown>;
  const raw = record.knownBy ?? record.known_by ?? record.characters;
  if (!Array.isArray(raw)) return ['cast'];
  return raw
    .map((name) => (typeof name === 'string' ? name.trim() : ''))
    .filter(Boolean);
}

export function extractCanonSourceChapter(entry: unknown): number | undefined {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return undefined;
  const record = entry as CanonRecord<string> | Record<string, unknown>;
  const raw = (record as CanonRecord<string>).sourceChapterId ?? record.sourceChapter ?? record.chapterNumber;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

export function extractCanonSourceRecordId(entry: unknown, fallbackId: string): string | undefined {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return fallbackId;
  const record = entry as Record<string, unknown>;
  const raw = record.id ?? record.recordId ?? record.factId ?? record.canonId;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : fallbackId;
}

export function assertPromptSafeStatement(statement: string, sourceField: string): void {
  if (statement.includes('[object Object]')) {
    throw new ChapterCanonPreflightError(
      `Canon normalization produced an unsafe prompt fragment for ${sourceField}.`,
      { sourceField, runtimeType: 'string', objectKeys: ['[object Object]'] },
    );
  }
}

export function normalizeCanonInputArray(params: {
  entries: unknown[] | undefined;
  sourceField: string;
  chapterNumber?: number;
  idPrefix: string;
  required?: boolean;
}): {
  normalized: NormalizedCanonEntry[];
  diagnostic: CanonInputShapeDiagnostic;
  warnings: string[];
} {
  const entries = Array.isArray(params.entries) ? params.entries : [];
  const runtimeTypes: string[] = [];
  const objectKeySets: string[][] = [];
  const normalized: NormalizedCanonEntry[] = [];
  const warnings: string[] = [];
  let rejectedCount = 0;

  entries.forEach((entry, index) => {
    const runtimeType = runtimeTypeOf(entry);
    runtimeTypes.push(runtimeType);
    if (runtimeType === 'object') {
      objectKeySets.push(objectKeys(entry));
    } else {
      objectKeySets.push([]);
    }

    if (entry == null) {
      rejectedCount += 1;
      return;
    }

    const statement = extractCanonStatement(entry);
    if (!statement) {
      rejectedCount += 1;
      if (runtimeType === 'object') {
        warnings.push(
          `${params.sourceField}[${index}] unsupported object shape keys=${objectKeys(entry).join('|') || 'none'}`,
        );
      } else {
        warnings.push(`${params.sourceField}[${index}] unsupported runtimeType=${runtimeType}`);
      }
      return;
    }

    assertPromptSafeStatement(statement, params.sourceField);
    const factId = `${params.idPrefix}_${index + 1}`;
    normalized.push({
      factId,
      statement,
      knownBy: extractCanonKnownBy(entry),
      sourceChapter: extractCanonSourceChapter(entry),
      sourceRecordId: extractCanonSourceRecordId(entry, factId),
    });
  });

  const diagnostic: CanonInputShapeDiagnostic = {
    chapterNumber: params.chapterNumber,
    sourceField: params.sourceField,
    entryCount: entries.length,
    runtimeTypes,
    objectKeySets,
    normalizedCount: normalized.length,
    rejectedCount,
  };

  if (params.required && entries.length > 0 && normalized.length === 0) {
    throw new ChapterCanonPreflightError(
      `Required canon input ${params.sourceField} could not be normalized for chapter ${params.chapterNumber ?? 'unknown'}.`,
      {
        sourceField: params.sourceField,
        chapterNumber: params.chapterNumber,
        runtimeType: runtimeTypes.join(','),
        objectKeys: objectKeySets.flat(),
      },
    );
  }

  return { normalized, diagnostic, warnings };
}

export function logChapterCanonInputShapes(diagnostics: CanonInputShapeDiagnostic[]): void {
  if (!diagnostics.length) return;
  console.log(`[CHAPTER CANON INPUT SHAPES] ${JSON.stringify(diagnostics)}`);
}

export function logChapterCanonNormalizationWarnings(warnings: string[]): void {
  for (const warning of warnings) {
    console.warn(`[CHAPTER CANON NORMALIZATION WARNING] ${warning}`);
  }
}

export function formatCanonEntriesForPrompt(
  entries: unknown[] | undefined,
  options?: {
    sourceField?: string;
    idPrefix?: string;
    separator?: string;
    emptyLabel?: string;
  },
): string {
  const sourceField = options?.sourceField ?? 'ledger.resolved_plot_points';
  const idPrefix = options?.idPrefix ?? 'prompt';
  const separator = options?.separator ?? ', ';
  const emptyLabel = options?.emptyLabel ?? 'None';

  const { normalized } = normalizeCanonInputArray({
    entries,
    sourceField,
    idPrefix,
  });
  if (!normalized.length) return emptyLabel;
  return normalized.map((entry) => entry.statement).join(separator);
}

export function formatCanonEntriesMultiline(
  entries: unknown[] | undefined,
  sourceField: string,
): string {
  const { normalized } = normalizeCanonInputArray({
    entries,
    sourceField,
    idPrefix: 'multiline',
  });
  if (!normalized.length) return '';
  return normalized.map((entry) => entry.statement).join('\n- ');
}

export function formatCanonLocksForPrompt(raw: unknown, sourceField = 'storyBible.canonLocks'): string {
  if (raw == null) return '';
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed || '';
  }
  if (Array.isArray(raw)) {
    return formatCanonEntriesForPrompt(raw, { sourceField, separator: '\n', emptyLabel: '' });
  }
  const statement = extractCanonStatement(raw);
  return statement || '';
}

export function normalizeCharacterNameList(values: unknown[] | undefined): string[] {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => {
      if (typeof value === 'string') return value.trim();
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        const record = value as Record<string, unknown>;
        return readStringField(record.name) || readStringField(record.character) || readStringField(record.content) || '';
      }
      return '';
    })
    .filter(Boolean);
}

export function normalizeBeatText(value: unknown): string | null {
  return extractCanonStatement(value);
}
